import React, { useState, useEffect } from 'react';
import { useStore } from '../store/StoreContext';
import { useToast } from '../hooks/useToast';
import { IconPlus, IconTruck } from '../components/icons';
import { Button, EntityCard, EntityAvatar, StatusPill, cardGridCls, PageHeader, EmptyState, pageWrapCls, DatePicker, Input, IconButton } from '../components/ui';
import TruckModal from '../components/modals/TruckModal';
import TruckServiceModal from '../components/modals/TruckServiceModal';
import db from '../lib/database';
import { fmtShortDate, todayYmd } from '../lib/moves';

// Fleet page — truck cards + maintenance tracking (sql/075, 2026-08-03).
// Each card badges its upkeep state: tag/registration (amber ≤30 days
// out, red past due) and service next-due (latest entry per service
// type carries the active due date). The 🔧 Service button opens the
// per-truck panel (log + tag expiration); clicking the card still opens
// the plain truck editor.
//
// Out of service (truck_downtime, 2026-09-18 — moves-architecture.md §2):
// the CALENDAR side of maintenance. A From/To range + reason per truck;
// the truck board greys those days ("Out — brakes") and Capacity drops
// the truck from the day's count. The maintenance log above stays the
// record of what was done; this is "don't plan on it".

const DAY_MS = 86400000;

// Inline "Out of service" section under a truck card: current/future
// ranges with a remove ✕, plus a folded From / To / reason form.
function TruckDowntimeSection({ truck, rows, onSave, onRemove, showToast }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ from_date: '', to_date: '', reason: '' });
    const [saving, setSaving] = useState(false);
    const today = todayYmd();
    // Only rows still ahead of us (a range that ended is history — the
    // service log is the record of that).
    const live = rows.filter(r => (r.to_date || r.from_date) >= today).sort((a, b) => a.from_date.localeCompare(b.from_date));
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.from_date) { showToast('Pick the first day out', 'error'); return; }
        const to = form.to_date && form.to_date >= form.from_date ? form.to_date : form.from_date;
        setSaving(true);
        try {
            await onSave({ truck_id: truck.id, from_date: form.from_date, to_date: to, reason: form.reason.trim() || null });
            showToast(`${truck.name} out ${fmtShortDate(form.from_date)}${to !== form.from_date ? ` – ${fmtShortDate(to)}` : ''}`);
            setForm({ from_date: '', to_date: '', reason: '' });
            setOpen(false);
        } catch (e) {
            showToast('Error: ' + (e?.message || e), 'error');
        } finally { setSaving(false); }
    };

    return (
        // stopPropagation: the whole card opens the truck editor on click.
        <div className="text-xs" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Out of service</span>
                {!open && <Button variant="tertiary" size="sm" className="-my-1" onClick={() => setOpen(true)}>+ Mark out</Button>}
            </div>
            {live.length > 0 && (
                <div className="mt-1 space-y-1">
                    {live.map(r => (
                        <div key={r.id} className="flex items-center gap-2 rounded-md bg-slate-50 border border-slate-200 px-2 py-1">
                            <span className="font-semibold text-slate-700 tabular-nums whitespace-nowrap">{fmtShortDate(r.from_date)}{r.to_date && r.to_date !== r.from_date ? ` – ${fmtShortDate(r.to_date)}` : ''}</span>
                            <span className="text-slate-500 truncate flex-1">{r.reason || 'out'}</span>
                            <IconButton label={`Remove ${fmtShortDate(r.from_date)} downtime`} size="sm" onClick={async () => { try { await onRemove(r.id); showToast('Downtime removed'); } catch (e) { showToast('Error: ' + (e?.message || e), 'error'); } }}>✕</IconButton>
                        </div>
                    ))}
                </div>
            )}
            {live.length === 0 && !open && <div className="mt-1 text-slate-400">In service — nothing scheduled.</div>}
            {open && (
                <div className="mt-2 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                        <DatePicker size="sm" value={form.from_date} onChange={(v) => { set('from_date', v); if (form.to_date && v && form.to_date < v) set('to_date', v); }} placeholder="From" ariaLabel="First day out" ignoreFlags clearable={false} />
                        <DatePicker size="sm" value={form.to_date} onChange={(v) => set('to_date', v)} placeholder="To (same day)" ariaLabel="Last day out" min={form.from_date || undefined} ignoreFlags />
                    </div>
                    <Input value={form.reason} onChange={(e) => set('reason', e.target.value)} placeholder="Why — brakes, tires, inspection…" aria-label="Reason" />
                    <div className="flex items-center justify-end gap-2">
                        <Button variant="tertiary" size="sm" onClick={() => { setOpen(false); setForm({ from_date: '', to_date: '', reason: '' }); }}>Cancel</Button>
                        <Button size="sm" loading={saving} onClick={save}>Save</Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function FleetPage() {
    const { trucks, jobs, saveTruck, deactivateTruck, truckDowntime, saveTruckDowntime, removeTruckDowntime } = useStore();
    const [modal, setModal] = useState(null);
    const [serviceModal, setServiceModal] = useState(null); // truck id
    const [serviceLog, setServiceLog] = useState([]);
    const { show: showToast } = useToast();

    const loadServiceLog = async () => {
        try {
            setServiceLog(await db.getTruckServiceLog());
        } catch (e) {
            console.warn('service log load failed:', e?.message || e);
        }
    };
    useEffect(() => { loadServiceLog(); }, []);

    const handleSave = async (form) => { await saveTruck(form); showToast(form.id ? 'Updated' : 'Added'); };
    const handleDelete = async (id) => { await deactivateTruck(id); showToast('Truck removed from fleet'); };
    const getTruckJobs = (id) => jobs.filter(j => (j.truck_ids || []).includes(id)).length;

    const todayIso = new Date().toLocaleDateString('en-CA');
    const soonIso = new Date(Date.now() + 30 * DAY_MS).toLocaleDateString('en-CA');

    // Upkeep badges per truck: tag state + service items due/coming due.
    // "Active" next-due per service type = the LATEST entry of that type
    // (an older oil change's due date is superseded by the newer one).
    const upkeepFor = (truck) => {
        const badges = [];
        if (truck.needs_service) {
            badges.push({ level: 'red', label: `Needs service${truck.service_issue ? `: ${truck.service_issue}` : ''}` });
        }
        if (truck.tag_expiration) {
            if (truck.tag_expiration < todayIso) badges.push({ level: 'red', label: 'Tag expired' });
            else if (truck.tag_expiration <= soonIso) badges.push({ level: 'amber', label: `Tag due ${new Date(truck.tag_expiration + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` });
        }
        const latestByType = new Map();
        for (const e of serviceLog) {
            if (e.truck_id !== truck.id) continue;
            if (!latestByType.has(e.service_type)) latestByType.set(e.service_type, e); // log is sorted date desc
        }
        for (const [type, e] of latestByType) {
            if (!e.next_due_date) continue;
            if (e.next_due_date < todayIso) badges.push({ level: 'red', label: `${type} overdue` });
            else if (e.next_due_date <= soonIso) badges.push({ level: 'amber', label: `${type} due soon` });
        }
        return badges;
    };

    const serviceTruck = serviceModal ? trucks.find(t => t.id === serviceModal) : null;

    // Header summary (page-headers.md: counts, not prose) — trucks, how
    // many need service, how many have an overdue/upcoming upkeep item.
    const needsServiceCount = trucks.filter(t => t.needs_service).length;
    const upkeepDueCount = trucks.filter(t => !t.needs_service && upkeepFor(t).length > 0).length;
    const summary = [
        `${trucks.length} truck${trucks.length === 1 ? '' : 's'}`,
        `${needsServiceCount} need${needsServiceCount === 1 ? 's' : ''} service`,
        ...(upkeepDueCount > 0 ? [`${upkeepDueCount} upkeep due`] : []),
    ].join(' · ');

    return (
        <div className={pageWrapCls}>
            <PageHeader
                title="Fleet"
                summary={summary}
                help={<>
                    <p>Every truck in the fleet with its upkeep state. The badge reads Ready, Due soon, Overdue or Needs service from the tag expiration and the latest service entry of each type.</p>
                    <p className="mt-2">Click a card to edit the truck; Service opens the maintenance log, the needs-service flag and the tag expiration.</p>
                    <p className="mt-2"><span className="font-semibold">Out of service</span> (on each card) is the calendar side: a From / To range and a reason. The truck board greys those days with the reason and Capacity leaves the truck out of that day's count. Remove a range with ✕ when the truck is back early.</p>
                </>}
                actions={<Button variant="primary" leftIcon={<IconPlus size={18}/>} onClick={() => setModal('new')}>Add Truck</Button>}
            />
            {trucks.length === 0 && (
                <EmptyState
                    icon={<IconTruck size={28} />}
                    title="No trucks yet"
                    hint="Add your first truck to assign it on the dispatch board and track its upkeep here."
                    action={<Button size="sm" leftIcon={<IconPlus size={16}/>} onClick={() => setModal('new')}>Add Truck</Button>}
                />
            )}
            {trucks.length > 0 && <div className={cardGridCls}>
                {trucks.map(truck => {
                    const badges = upkeepFor(truck);
                    // Header pill = the truck's overall upkeep state; the
                    // individual ⚠ items stay as chips in the body.
                    const hasRed = badges.some(b => b.level === 'red');
                    // Out of service TODAY (truck_downtime) outranks the upkeep pill — it's the calendar fact.
                    const outToday = (truckDowntime || []).some(r => r.truck_id === truck.id && r.from_date && r.from_date <= todayIso && (r.to_date || r.from_date) >= todayIso);
                    const status = outToday
                        ? { label: 'Out today', color: '#475569' }
                        : truck.needs_service
                        ? { label: 'Needs service', color: '#dc2626' }
                        : hasRed ? { label: 'Overdue', color: '#dc2626' }
                        : badges.length > 0 ? { label: 'Due soon', color: '#d97706' }
                        : { label: 'Ready', color: '#059669' };
                    const facts = [
                        { label: 'Number', value: `#${truck.number}`, mono: true },
                        { label: 'Type', value: truck.type },
                        { label: 'Jobs', value: `${getTruckJobs(truck.id)} jobs` },
                    ];
                    if (truck.tag_expiration) facts.push({ label: 'Tag good thru', value: new Date(truck.tag_expiration + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) });
                    if (truck.packing_vehicle) facts.push({ label: 'Packing vehicle', value: <span title="Counts toward the packing-vehicle requirement on PACK jobs">📦 Yes</span> });
                    return (
                        // One card recipe — lists.md §4 (EntityCard owns padding, radius, hover).
                        <EntityCard
                            key={truck.id}
                            avatar={<EntityAvatar square text={<IconTruck size={18}/>} color="#475569" />}
                            title={truck.name}
                            subtitle={`#${truck.number} · ${truck.type}`}
                            badge={<StatusPill label={status.label} color={status.color} />}
                            facts={facts}
                            onClick={() => setModal(truck)}
                            footer={<>
                                <Button variant="tertiary" size="sm" onClick={(e) => { e.stopPropagation(); setModal(truck); }}>Edit</Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); setServiceModal(truck.id); }}>
                                    🔧 Service
                                </Button>
                            </>}
                        >
                            {badges.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {badges.map((b, i) => (
                                        <span key={i} title={b.label} className="max-w-full overflow-hidden"><StatusPill label={`⚠ ${b.label}`} color={b.level === 'red' ? '#dc2626' : '#b45309'} /></span>
                                    ))}
                                </div>
                            )}
                            {(truck.tags || []).length > 0 && <div className="flex flex-wrap gap-1">{truck.tags.map(tag => <span key={tag} className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-md">{tag}</span>)}</div>}
                            <TruckDowntimeSection
                                truck={truck}
                                rows={(truckDowntime || []).filter(r => r.truck_id === truck.id)}
                                onSave={saveTruckDowntime}
                                onRemove={removeTruckDowntime}
                                showToast={showToast}
                            />
                        </EntityCard>
                    );
                })}
            </div>}
            {modal && <TruckModal truck={modal === 'new' ? null : modal} onClose={() => setModal(null)} onSave={async (form) => { await handleSave(form); setModal(null); }} onDelete={async (id) => { await handleDelete(id); setModal(null); }} />}
            {serviceTruck && (
                <TruckServiceModal
                    truck={serviceTruck}
                    entries={serviceLog.filter(e => e.truck_id === serviceTruck.id)}
                    onClose={() => setServiceModal(null)}
                    onSaveTruck={saveTruck}
                    onChanged={loadServiceLog}
                    showToast={showToast}
                />
            )}
        </div>
    );
}

export default FleetPage;
